import React, { useCallback, useEffect, useRef, useState } from "react";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  makeStyles,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { Close, Edit } from "@material-ui/icons";
import CircularCenteredLoader from "features/common/CircularCenteredLoader";
import GlobalSearch from "../../Components/GlobalSearch/GlobalSearch";
import {
  createOrderShortcut,
  deleteOrderShortcut,
  getOrderShortcutMasters,
  updateOrderShortcut,
} from "../../service";

/**
 * Shortcut Keys master (Order Drafting shortcuts): the abbreviations a drafter
 * types (followed by a space) in the Order Drafting editor to insert their
 * expansion. Looks and works like <BacMasterPanel />, but this API has its own
 * shape: a plain `{ success, data: [...] }` list with no pagination, PATCH
 * writes, per-field `errors` on a 400, and an Active switch that saves straight
 * from the row.
 *
 * The API also takes an `order`; it isn't offered here, so a new shortcut
 * gets the default (0) and an edit leaves the existing value alone.
 *
 * The editor fetches the active shortcuts when it opens, so a change here
 * reaches drafters the next time they open it.
 */

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "true", label: "Active" },
  { value: "false", label: "Inactive" },
];

// Mirrors the backend's rules so most mistakes are caught before saving; the
// API's own messages (e.g. a duplicate abbreviation) land in the same spots.
const validate = (values) => {
  const errors = {};
  const abbreviation = String(values.abbreviation || "").trim();
  const expansion = String(values.expansion || "").trim();

  if (!abbreviation) errors.abbreviation = "Abbreviation is required.";
  else if (/\s/.test(abbreviation)) errors.abbreviation = "Abbreviation must be a single word without spaces.";
  else if (abbreviation.length > 32) errors.abbreviation = "Abbreviation can be at most 32 characters.";

  if (!expansion) errors.expansion = "Expansion is required.";
  else if (/[\r\n]/.test(expansion)) errors.expansion = "Expansion must be a single line.";
  else if (expansion.length > 1000) errors.expansion = "Expansion can be at most 1000 characters.";
  return errors;
};

const toPayload = (values) => ({
  abbreviation: String(values.abbreviation || "").trim(),
  expansion: String(values.expansion || "").trim(),
});

const EMPTY_ROW = { abbreviation: "", expansion: "", is_active: true };

// The API's per-field `errors` and/or a single message.
const readError = (error) => {
  const data = error?.response?.data;
  return {
    status: error?.response?.status,
    fields: data?.errors && typeof data.errors === "object" ? data.errors : {},
    message: data?.error || data?.detail || data?.message || "",
  };
};

// created_at / updated_at are UTC ISO timestamps; show the IST date.
const formatIstDate = (iso) => {
  if (!iso) return "—";
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB", { timeZone: "Asia/Kolkata" });
};

const useStyles = makeStyles((theme) => ({
  panel: {
    width: "100%",
    margin: "0 auto",
  },
  // Title + hint on the left, filters on the right; when they wrap, the
  // filters drop below the hint with a clear gap.
  header: {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(2),
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "flex-end",
    gap: `${theme.spacing(2.5)}px ${theme.spacing(3)}px`,
  },
  title: {
    fontSize: "1.2rem",
    fontWeight: 600,
    color: TEXT.strong,
  },
  hint: {
    color: TEXT.muted,
    fontSize: "0.81rem",
    marginTop: 2,
  },
  paper: {
    padding: theme.spacing(3),
    borderRadius: 10,
    width: "100%",
    boxSizing: "border-box",
  },
  addForm: {
    marginBottom: theme.spacing(1),
    borderBottom: "1px solid #E4E7EC",
  },
  addButton: {
    borderRadius: 4,
    height: 36,
    width: "100%",
  },
  switchRow: {
    display: "flex",
    alignItems: "center",
    height: 40,
  },
  tableContainer: {
    boxShadow: "none",
    border: "1px solid #EAECF0",
    borderRadius: 8,
  },
  headCell: {
    fontWeight: 600,
    color: TEXT.strong,
    fontSize: "0.81rem",
    background: "#FAFAFB",
    whiteSpace: "nowrap",
    padding: "6px 10px",
  },
  cell: {
    fontSize: "0.875rem",
    color: TEXT.strong,
    padding: "6px 10px",
    verticalAlign: "top",
  },
  expansionCell: {
    minWidth: 240,
    overflowWrap: "anywhere",
  },
  abbreviation: {
    fontFamily: "monospace",
    fontSize: "0.875rem",
    fontWeight: 600,
    background: "#F4F3FF",
    color: TEXT.brand,
    padding: "2px 6px",
    borderRadius: 4,
    whiteSpace: "nowrap",
  },
  actionBtn: {
    background: "#F4F8FF",
    borderRadius: 4,
    color: TEXT.strong,
    fontSize: "0.8rem",
    padding: "4px 8px",
  },
  iconActionBtn: {
    background: "#F4F8FF",
    borderRadius: 4,
    color: TEXT.strong,
    padding: 6,
  },
  emptyState: {
    padding: theme.spacing(5),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  filter: {
    minWidth: 150,
    background: "#fff",
  },
  deleteButton: {
    color: "#fff",
    background: TEXT.error,
    "&:hover": { background: "#B71C1C" },
  },
}));

const OrderShortcutsPanel = ({ showSnackbar }) => {
  const classes = useStyles();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");

  const [newRow, setNewRow] = useState(EMPTY_ROW);
  const [addErrors, setAddErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [editId, setEditId] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [editErrors, setEditErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [togglingId, setTogglingId] = useState(null);
  const [confirmRow, setConfirmRow] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Only the latest list request may fill the table (search is debounced but
  // responses can still come back out of order).
  const requestRef = useRef(0);

  const fetchList = useCallback(async (searchText = search, statusValue = status) => {
    const requestId = ++requestRef.current;
    setLoading(true);
    try {
      const params = {};
      if (searchText) params.search = searchText;
      if (statusValue !== "all") params.is_active = statusValue;
      const res = await getOrderShortcutMasters(params);
      if (requestId !== requestRef.current) return;
      setRows(Array.isArray(res?.data?.data) ? res.data.data : []);
    } catch (error) {
      if (requestId !== requestRef.current) return;
      console.error("Error fetching order shortcuts:", error);
      showSnackbar(readError(error).message || "Could not load the shortcuts.", "error");
      setRows([]);
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [search, status, showSnackbar]);

  useEffect(() => {
    fetchList("", "all");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = (value) => {
    setSearch(value);
    fetchList(value, status);
  };

  const handleStatusChange = (value) => {
    setStatus(value);
    fetchList(search, value);
  };

  // A 404 means someone else deleted it meanwhile; refresh to drop the row.
  const reportWriteError = (error, fallback) => {
    const { status: code, message } = readError(error);
    if (code === 404) {
      showSnackbar("This shortcut no longer exists. The list has been refreshed.", "error");
      fetchList();
      return;
    }
    showSnackbar(message || fallback, "error");
  };

  // ---- add ------------------------------------------------------------------

  const changeNewRow = (key, value) => {
    setNewRow((prev) => ({ ...prev, [key]: value }));
    if (addErrors[key]) setAddErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const handleAdd = async () => {
    const errors = validate(newRow);
    setAddErrors(errors);
    if (Object.keys(errors).length) return;

    setSubmitting(true);
    try {
      await createOrderShortcut({ ...toPayload(newRow), is_active: Boolean(newRow.is_active) });
      showSnackbar("Shortcut added.", "success");
      setNewRow(EMPTY_ROW);
      setAddErrors({});
      fetchList();
    } catch (error) {
      console.error("Error adding order shortcut:", error);
      const { fields } = readError(error);
      if (Object.keys(fields).length) setAddErrors(fields);
      else reportWriteError(error, "Could not add the shortcut. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ---- edit -----------------------------------------------------------------

  const startEdit = (row) => {
    setEditId(row.id);
    setEditRow({ abbreviation: row.abbreviation || "", expansion: row.expansion || "" });
    setEditErrors({});
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditRow(null);
    setEditErrors({});
  };

  const changeEditRow = (key, value) => {
    setEditRow((prev) => ({ ...prev, [key]: value }));
    if (editErrors[key]) setEditErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const saveEdit = async () => {
    const errors = validate(editRow);
    setEditErrors(errors);
    if (Object.keys(errors).length) return;

    setSaving(true);
    try {
      await updateOrderShortcut(editId, toPayload(editRow));
      showSnackbar("Shortcut updated.", "success");
      cancelEdit();
      fetchList();
    } catch (error) {
      console.error("Error updating order shortcut:", error);
      const { fields } = readError(error);
      if (Object.keys(fields).length) setEditErrors(fields);
      else reportWriteError(error, "Could not update the shortcut. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // ---- active toggle / delete -------------------------------------------------

  const setActive = async (row, isActive) => {
    setTogglingId(row.id);
    try {
      await updateOrderShortcut(row.id, { is_active: isActive });
      showSnackbar(
        isActive ? `“${row.abbreviation}” is active again.` : `“${row.abbreviation}” deactivated.`,
        "success"
      );
      fetchList();
    } catch (error) {
      console.error("Error updating order shortcut:", error);
      reportWriteError(error, "Could not change the shortcut. Please try again.");
    } finally {
      setTogglingId(null);
    }
  };

  const handleDeactivateInstead = async () => {
    const row = confirmRow;
    setConfirmRow(null);
    await setActive(row, false);
  };

  const handleDelete = async () => {
    const row = confirmRow;
    setDeleting(true);
    try {
      await deleteOrderShortcut(row.id);
      showSnackbar(`Shortcut “${row.abbreviation}” deleted.`, "success");
      setConfirmRow(null);
      fetchList();
    } catch (error) {
      console.error("Error deleting order shortcut:", error);
      setConfirmRow(null);
      reportWriteError(error, "Could not delete the shortcut. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  // ---- rendering --------------------------------------------------------------

  const textInput = (key, label, values, errors, onChange, extra = {}) => (
    <TextField
      fullWidth
      size="small"
      variant="outlined"
      label={label}
      value={values[key] ?? ""}
      onChange={(e) => onChange(key, e.target.value)}
      error={Boolean(errors[key])}
      helperText={errors[key] || " "}
      InputProps={{ style: { fontSize: "0.8rem" } }}
      InputLabelProps={{ style: { fontSize: "0.8rem" } }}
      {...extra}
    />
  );

  const emptyMessage = search || status !== "all"
    ? "No shortcuts match these filters."
    : "No shortcuts yet.";

  return (
    <Box className={classes.panel}>
      <Box className={classes.header}>
        <Box>
          <Typography className={classes.title}>Shortcut Keys Master</Typography>
          <Typography className={classes.hint}>
            In the Order Drafting editor, typing an abbreviation followed by a space inserts its expansion.
            Changes apply the next time a drafter opens the editor.
          </Typography>
        </Box>
        <Box>
          <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 12 }}>
            <TextField
              select
              size="small"
              variant="outlined"
              label="Status"
              className={classes.filter}
              value={status}
              onChange={(e) => handleStatusChange(e.target.value)}
              InputProps={{ style: { fontSize: "0.8rem" } }}
              InputLabelProps={{ style: { fontSize: "0.8rem" } }}
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} style={{ fontSize: "0.8rem" }}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <GlobalSearch
              value={search}
              onSearchChange={handleSearchChange}
              placeholder="Search shortcuts..."
            />
          </Box>
        </Box>
      </Box>

      <Paper className={classes.paper}>
        {/* Add row */}
        <Grid container spacing={2} alignItems="flex-start" className={classes.addForm}>
          <Grid item xs={12} sm={6} md={3}>
            {textInput("abbreviation", "Abbreviation", newRow, addErrors, changeNewRow, {
              required: true,
              placeholder: "e.g. sc",
              inputProps: { maxLength: 32 },
            })}
          </Grid>
          <Grid item xs={12} sm={6} md={5}>
            {textInput("expansion", "Expansion", newRow, addErrors, changeNewRow, {
              required: true,
              placeholder: "e.g. Statement of Claim",
              inputProps: { maxLength: 1000 },
            })}
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Box className={classes.switchRow}>
              <Switch
                color="primary"
                checked={Boolean(newRow.is_active)}
                onChange={(e) => changeNewRow("is_active", e.target.checked)}
              />
              <Typography variant="body2">{newRow.is_active ? "Active" : "Inactive"}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="contained"
              color="primary"
              className={classes.addButton}
              endIcon={<span style={{ fontSize: 18, lineHeight: 1 }}>+</span>}
              disabled={submitting}
              onClick={handleAdd}
              size="small"
            >
              {submitting ? "Adding…" : "Add"}
            </Button>
          </Grid>
        </Grid>

        {/* List */}
        {loading ? (
          <CircularCenteredLoader />
        ) : rows.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography variant="body1">{emptyMessage}</Typography>
            {!search && status === "all" && (
              <Typography variant="body2">Add one using the form above to get started.</Typography>
            )}
          </Box>
        ) : (
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table size="small" style={{ maxWidth: "100%", height: "auto", marginTop: 0 }}>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.headCell}>S.No.</TableCell>
                  <TableCell className={classes.headCell}>Abbreviation</TableCell>
                  <TableCell className={classes.headCell}>Expansion</TableCell>
                  <TableCell className={classes.headCell}>Active</TableCell>
                  <TableCell className={classes.headCell}>Last Updated</TableCell>
                  <TableCell className={classes.headCell} align="center">Modify</TableCell>
                  <TableCell className={classes.headCell} align="center">Delete</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => {
                  const isEditing = editId === row.id;
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell className={classes.cell}>{index + 1}</TableCell>
                      <TableCell className={classes.cell} style={isEditing ? { minWidth: 140 } : undefined}>
                        {isEditing
                          ? textInput("abbreviation", "", editRow, editErrors, changeEditRow, { inputProps: { maxLength: 32 } })
                          : <span className={classes.abbreviation}>{row.abbreviation}</span>}
                      </TableCell>
                      <TableCell className={`${classes.cell} ${classes.expansionCell}`}>
                        {isEditing
                          ? textInput("expansion", "", editRow, editErrors, changeEditRow, { inputProps: { maxLength: 1000 } })
                          : row.expansion}
                      </TableCell>
                      <TableCell className={classes.cell}>
                        <Switch
                          size="small"
                          color="primary"
                          checked={Boolean(row.is_active)}
                          disabled={togglingId === row.id || isEditing}
                          onChange={(e) => setActive(row, e.target.checked)}
                          inputProps={{ "aria-label": `${row.abbreviation} active` }}
                        />
                      </TableCell>
                      <TableCell className={classes.cell} style={{ whiteSpace: "nowrap" }}>
                        {formatIstDate(row.updated_at)}
                      </TableCell>
                      <TableCell align="center">
                        {isEditing ? (
                          <Box display="flex" justifyContent="center" style={{ gap: 8 }}>
                            <IconButton size="small" className={classes.actionBtn} onClick={saveEdit} disabled={saving} title="Save">
                              Save
                            </IconButton>
                            <IconButton size="small" className={classes.actionBtn} onClick={cancelEdit} title="Cancel">
                              <Close style={{ height: 18, width: 18 }} />
                            </IconButton>
                          </Box>
                        ) : (
                          <IconButton
                            size="small"
                            title="Modify"
                            aria-label="Modify"
                            className={classes.iconActionBtn}
                            onClick={() => startEdit(row)}
                          >
                            <Edit style={{ height: 18, width: 18 }} />
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          title="Delete"
                          aria-label="Delete"
                          className={classes.iconActionBtn}
                          disabled={isEditing}
                          onClick={() => setConfirmRow(row)}
                        >
                          <DeleteOutlineIcon style={{ height: 18, width: 18 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={Boolean(confirmRow)} onClose={() => !deleting && setConfirmRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete shortcut “{confirmRow?.abbreviation}”?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" style={{ color: TEXT.secondary }}>
            Drafters will no longer be able to use it, and this can't be undone.
            {confirmRow?.is_active && " To keep it for later, deactivate it instead: it can be switched back on at any time."}
          </Typography>
        </DialogContent>
        {/* Small buttons so all three fit on one row of the xs dialog. */}
        <DialogActions style={{ padding: "8px 24px 16px" }}>
          <Button size="small" onClick={() => setConfirmRow(null)} disabled={deleting}>Cancel</Button>
          {confirmRow?.is_active && (
            <Button size="small" variant="outlined" color="primary" onClick={handleDeactivateInstead} disabled={deleting}>
              Deactivate instead
            </Button>
          )}
          <Button size="small" variant="contained" className={classes.deleteButton} onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderShortcutsPanel;
